
// Microphone constraints for pronunciation recording.
//
// getUserMedia turns on echoCancellation / noiseSuppression / autoGainControl by
// default -- DSP tuned for voice/video calls that distorts speech for the
// phoneme scorer. Echo cancellation and noise suppression are off so the browser
// capture matches a plain voice recorder, which the model transcribes cleanly.
// Auto-gain stays ON: quiet readers on low-gain laptop mics were being captured
// too softly to score. channelCount 1 keeps it mono.
export const MIC_AUDIO_CONSTRAINTS = {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: true,
    channelCount: 1,
};

// The browser's default Opus bitrate can be low; give speech enough headroom.
export const RECORDER_OPTIONS = { audioBitsPerSecond: 128000 };

// Containers we ask MediaRecorder for, best first. Chrome/Edge/Firefox take the
// WebM/Opus entries; Safari only supports audio/mp4. Passing no mimeType at all
// works too, but then we have no idea what we got -- and the recording ends up
// labelled with whatever the caller guessed, which is how every upload came to be
// called "audio.wav" while actually containing WebM.
const PREFERRED_MIME_TYPES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
];

// The first container this browser can actually record, or undefined to let the
// browser choose (older browsers with no isTypeSupported).
export const pickRecorderMimeType = () => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
        return undefined;
    }
    return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
};

// MediaRecorder options including a supported mimeType. Prefer this over the bare
// RECORDER_OPTIONS so `recorder.mimeType` reports something we chose.
export const getRecorderOptions = () => {
    const mimeType = pickRecorderMimeType();
    return mimeType ? { ...RECORDER_OPTIONS, mimeType } : { ...RECORDER_OPTIONS };
};

// File extension matching a recorder/blob mime type. Uploads must carry an
// extension that matches their bytes: the server whitelists by extension, and a
// mislabelled file makes every log line and archived recording misleading.
export const extensionForMimeType = (mime = '') => {
    const base = String(mime).split(';')[0].trim().toLowerCase();
    return (
        {
            'audio/webm': '.webm',
            'video/webm': '.webm',
            'audio/ogg': '.ogg',
            'video/ogg': '.ogg',
            'audio/mp4': '.m4a',
            'video/mp4': '.mp4',
            'audio/wav': '.wav',
            'audio/wave': '.wav',
            'audio/x-wav': '.wav',
            'audio/mpeg': '.mp3',
        }[base] || '.webm'
    );
};

// True if the blob starts with a container header we recognise.
//
// A MediaRecorder blob ALWAYS begins with one. Bytes that don't are a fragment
// whose first chunk was lost -- ffmpeg rejects those with "Invalid data found
// when processing input", so there is nothing to gain by uploading them.
//
// Unlike recordedAudioHasSound this fails CLOSED, because a missing header is a
// certainty rather than a decode hiccup. The list stays permissive so a future
// codec change can't start rejecting good audio.
export const hasKnownAudioContainer = async (blob) => {
    if (!blob || !blob.size) return false;
    try {
        const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
        const at = (offset, ...bytes) =>
            bytes.every((b, i) => head[offset + i] === b);
        return (
            at(0, 0x1a, 0x45, 0xdf, 0xa3) || // WebM / Matroska (EBML)
            at(0, 0x52, 0x49, 0x46, 0x46) || // RIFF / WAV
            at(0, 0x4f, 0x67, 0x67, 0x53) || // Ogg
            at(0, 0x49, 0x44, 0x33) ||       // MP3 with an ID3 tag
            at(0, 0xff, 0xfb) ||             // bare MP3 frame
            at(4, 0x66, 0x74, 0x79, 0x70)    // ISO-BMFF: mp4 / m4a
        );
    } catch (e) {
        return true; // can't read it here != unusable; don't block on our own bug
    }
};

// Request the microphone with the pronunciation-friendly constraints above.
// Use everywhere we record audio for evaluation so captures stay consistent.
export const getMicrophoneStream = () =>
    navigator.mediaDevices.getUserMedia({ audio: MIC_AUDIO_CONSTRAINTS });

// Create an AudioContext that is guaranteed to be running.
//
// Browsers suspend an AudioContext when the page is idle/backgrounded, and a
// freshly-created one can start "suspended" too. A suspended context feeds the
// AnalyserNode flat silence, so mic-level detection reads zero and wrongly
// reports "we couldn't detect any audio" after the mic has been unused a while.
// Always resume before wiring up the analyser. Best-effort: never throw.
export const createRunningAudioContext = async (options) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx(options);
    if (ctx.state === 'suspended') {
        try {
            await ctx.resume();
        } catch (e) {
            // resume() can reject if not in a user gesture; the caller is inside a
            // click handler so this is rare. Fall through with whatever state we got.
        }
    }
    return ctx;
};

// True if the RECORDED blob actually contains sound. Decodes the captured audio
// and checks its peak amplitude -- ground truth, unlike the live AnalyserNode
// used during recording, which can read silence (suspended context / warm-up)
// even when MediaRecorder captured real audio, wrongly tripping "no audio" on
// Done. Fails OPEN (returns true) on any decode error so a real recording is
// never blocked by a decode hiccup; the server rejects truly-bad audio anyway.
export const recordedAudioHasSound = async (blob, peakThreshold = 0.01) => {
    if (!blob || !blob.size) return false;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    let ctx;
    try {
        const arrayBuffer = await blob.arrayBuffer();
        ctx = new Ctx();
        const audio = await ctx.decodeAudioData(arrayBuffer);
        let peak = 0;
        for (let c = 0; c < audio.numberOfChannels; c++) {
            const data = audio.getChannelData(c);
            for (let i = 0; i < data.length; i++) {
                const v = data[i] < 0 ? -data[i] : data[i];
                if (v > peak) peak = v;
            }
        }
        return peak >= peakThreshold;
    } catch (e) {
        return true; // undecodable here != unusable on the server; don't block
    } finally {
        if (ctx) {
            try { ctx.close(); } catch (e) { /* ignore */ }
        }
    }
};

// Resolve once the stream's audio track is actually delivering audio. After a
// long idle a browser can hand back a track that is momentarily `muted`; wait
// for it to unmute (up to timeoutMs) so recording doesn't start on silence.
export const waitForLiveAudioTrack = (stream, timeoutMs = 1500) =>
    new Promise((resolve) => {
        const track = stream.getAudioTracks()[0];
        if (!track || !track.muted) {
            resolve();
            return;
        }
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            track.removeEventListener('unmute', finish);
            resolve();
        };
        track.addEventListener('unmute', finish);
        setTimeout(finish, timeoutMs);
    });

export const orderWordsBySentence = (sentence='', words=[]) => {
    // Find the position of each word in the sentence
    const wordPositions = words.map(word => ({
        word: word,
        position: sentence.indexOf(word)
    }));

    // Sort the words based on their positions
    wordPositions.sort((a, b) => a.position - b.position);

    // Extract the ordered words
    return wordPositions.map(item => item.word);
};
